"use client"

import { ReactElement, useCallback, useEffect, useState } from "react"
import {
  getAnalyticsConfig,
  hasConfiguredProvider,
} from "@modules/analytics/lib/config"
import {
  clearVendorCookies,
  readConsentFromDocument,
  serializeConsentCookie,
  shouldReloadAfterDecision,
} from "@modules/analytics/lib/consent"
import type { ConsentDecision } from "@modules/analytics/lib/consent"
import { CONSENT_CHANGED_EVENT } from "@modules/analytics/lib/constants"
import { clearTrackedPurchases } from "@modules/analytics/lib/purchase-dedup"
import {
  hasLoadedTagInDocument,
  resolveTags,
} from "@modules/analytics/lib/tags"
import {
  PRIVACY_POLICY_LABEL,
  PRIVACY_POLICY_PATH,
} from "@modules/legal/lib/legal-links"

const CONSENT_MESSAGE =
  "Usamos cookies de análise para medir o uso da loja e melhorar sua experiência."
const ACCEPT_LABEL = "Aceitar"
const DECLINE_LABEL = "Recusar"
const POLICY_LINK_ARIA_LABEL = `${PRIVACY_POLICY_LABEL} (abre em nova aba)`

/**
 * Banner de consentimento LGPD (Story 2.6, completado pela 10.10). Casca cliente sem lógica
 * de decisão própria: renderiza SÓ quando há TAG CARREGÁVEL configurado E o consentimento
 * ainda é "unknown" — não pede consentimento que não se usa. Com env vazia (hoje) nunca
 * aparece (dormant). "Aceitar"/"Recusar" gravam o cookie e escondem o banner.
 *
 * Story 10.10: liga à política publicada (fonte única `legal-links`), REAPARECE quando a
 * decisão é retirada no rodapé (escuta `CONSENT_CHANGED_EVENT`) e recarrega a página quando
 * a recusa chega com o tag já no DOM — injetar é reversível, desinjetar não é.
 */
export default function ConsentBanner(): ReactElement {
  // "unknown" no server e na 1ª renderização do cliente (evita mismatch de hidratação);
  // o efeito lê o cookie real só após montar, no browser.
  const [consent, setConsent] = useState<ConsentDecision>("unknown")
  const [isMounted, setIsMounted] = useState(false)

  const syncFromCookie = useCallback((): void => {
    setConsent(readConsentFromDocument())
  }, [])

  useEffect(() => {
    setIsMounted(true)
    syncFromCookie()
  }, [syncFromCookie])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    // O "Gerenciar cookies" do rodapé apaga o cookie e emite este evento: relemos a decisão
    // para o banner voltar sem exigir navegação (direito de retirada da LGPD).
    window.addEventListener(CONSENT_CHANGED_EVENT, syncFromCookie)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, syncFromCookie)
  }, [syncFromCookie])

  const providerConfigured = hasConfiguredProvider(getAnalyticsConfig())
  const isVisible = isMounted && providerConfigured && consent === "unknown"

  const decide = (decision: ConsentDecision): void => {
    // A decisão do cliente é gravada PRIMEIRO: tudo o que vem a seguir toca o DOM e o
    // `document.cookie` de terceiros, e uma exceção aí não pode custar a escolha que ele
    // acabou de fazer.
    if (typeof document !== "undefined") {
      document.cookie = serializeConsentCookie(decision)
    }
    setConsent(decision)
    if (decision === "denied") {
      // Recusar tem de RETIRAR: o banner reaparece quando já houve um "aceitar" anterior neste
      // browser (retirada pelo rodapé), logo os `_ga`/`_ga_<id>` do Google podem estar vivos —
      // e duram ~2 anos. Sem isto, "Recusar" e "Gerenciar cookies" implementavam duas
      // definições diferentes da mesma palavra.
      clearVendorCookies()
      clearTrackedPurchases()
    }
    const hasLoadedTag = hasLoadedTagInDocument(
      resolveTags(getAnalyticsConfig())
    )
    if (typeof window === "undefined") {
      return
    }
    // Notifica o loader (irmão no layout) para reavaliar o gate e emitir o page_view da
    // página atual — sem isto, uma sessão que consente na landing perderia essa 1ª view.
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
    if (shouldReloadAfterDecision(decision, hasLoadedTag)) {
      // Recusar só é verdade quando o terceiro deixa de existir na página; o <Script> já
      // injetado não se desinjeta.
      window.location.reload()
    }
  }

  return (
    // A região live está SEMPRE montada, vazia enquanto não há pergunta a fazer. Um leitor de
    // ecrã só anuncia alterações a uma região `aria-live` que JÁ existia no DOM — montar a
    // região junto com o conteúdo (o que o banner fazia) não anuncia nada, e era exatamente o
    // caso que motivou o atributo: o banner REAPARECER sem navegação, após "Gerenciar cookies".
    // Sem `role` nem `aria-label` no invólucro: um landmark permanente e vazio em todas as
    // páginas seria ruído de navegação.
    <div aria-live="polite">
      {isVisible && (
        <div
          role="region"
          aria-label="Consentimento de cookies"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-white/[0.08] bg-surface/95 backdrop-blur"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted">
              {CONSENT_MESSAGE}{" "}
              {/* Nova aba: a decisão em curso não se perde ao ler a política. O destino vem da
                  fonte única da 10.9 — nunca um literal novo, nunca "#".
                  Link INLINE dentro da frase, como no checkout e no registo (10.9): sem `min-h`
                  nem `inline-flex`, que esticariam a caixa de linha do parágrafo. O alvo de 44px
                  é dos BOTÕES, que são os controlos. */}
              <a
                href={PRIVACY_POLICY_PATH}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={POLICY_LINK_ARIA_LABEL}
                data-testid="consent-privacy-policy"
                className="font-semibold text-accent-strong underline underline-offset-4 transition-colors hover:text-accent"
              >
                {PRIVACY_POLICY_LABEL}
              </a>
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => decide("denied")}
                data-testid="consent-decline"
                className="min-h-[44px] rounded-[10px] border border-white/[0.08] bg-white/[0.05] px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-accent hover:text-white"
              >
                {DECLINE_LABEL}
              </button>
              <button
                type="button"
                onClick={() => decide("granted")}
                data-testid="consent-accept"
                className="min-h-[44px] rounded-[10px] bg-accent-strong px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
              >
                {ACCEPT_LABEL}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
